import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import './tabela_dinamica.css';
import { PermissionGate } from '../../contexts/PermissionGate';
 
interface TableColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  type?: 'percentage' | 'number';
  format?: boolean;
  colorCondition?: Record<string, string>;
  requiresPermission?: boolean;
  navigation?: {
    enabled: boolean;
    path: string;
    paramKey?: string;
  };
}
 
interface TableData {
  columns: TableColumn[];
  rows: Record<string, any>[];
}
 
interface TableConfig {
  search?: {
    enabled: boolean;
    placeholder?: string;
  };
  pagination?: {
    enabled: boolean;
    perPage?: number;
  };
  navigation?: {
    enabled: boolean;
    path: string;
    paramKey: string;
  };
  title?: string;
  permission?: string;
  subtitle?: string;
  button?: boolean;
  TextButton?: string;
  commandButton?: () => void;
}
 
const TabelaDinamica = ({ data, config, title, subtitle, button, TextButton, commandButton }: { data: TableData; config: TableConfig; title?: string; subtitle?: string, TextButton?: string,  button?: boolean, commandButton?: () => void }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({ key: null, direction: 'asc' });
  const navigate = useNavigate();
  const { id } = useParams();
 
  const filteredRows = data.rows.filter(row =>
    Object.values(row).some(val =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
 
  const perPage = config?.pagination?.perPage || 10;
  const totalPages = Math.ceil(filteredRows.length / perPage);
 
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );
 
  const sortedRows = [...paginatedRows].sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
 
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
 
  const handleNavigation = (row: any) => {
    if (config.navigation?.enabled) {
      const paramValue = row[config.navigation.paramKey];
      navigate(`${config.navigation.path}/${id}/${paramValue}`);
    }
  };
 
  const columns = config.navigation?.enabled
    ? [
        ...(config.permission ?
          [
            {
              key: 'actions',
              label: 'Detalhes',
              width: '80px',
              requiresPermission: true
            }
          ] : []
        ),
        ...data.columns
      ]
    : data.columns;
 
  return (
    <div className="outer-wrapper">
      <h1 className="title">{title}</h1>
      <span className="subtitle">{subtitle}</span>
 
      <div className='row'>
        <div className='col-6'>
          {config?.search?.enabled === true && (
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder={config.search.placeholder || "Buscar..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className='col-6'>
            {button && commandButton && (
              <div className="mb-3 text-end">
                <button className="btn btn-primary" onClick={commandButton}>
                  {TextButton}
                </button>
              </div>
          )}
        </div>
      </div>
 
      <div className="tabela-dinamica-wrapper">
        <table className="tabela-dinamica">
          <thead>
            <tr>
              {columns.map((column) => (
                column.requiresPermission && config.permission ? (
                  <PermissionGate key={column.key} required={config.permission}>
                    <th
                      style={{ width: column.width }}
                      className={column.sortable ? 'sortable' : ''}
                      data-sort={sortConfig.key === column.key ? sortConfig.direction : undefined}
                    >
                      {column.label}
                    </th>
                  </PermissionGate>
                ) : (
                  <th
                    key={column.key}
                    style={{ width: column.width }}
                    onClick={() => column.sortable && handleSort(column.key)}
                    className={column.sortable ? 'sortable' : ''}
                    data-sort={sortConfig.key === column.key ? sortConfig.direction : undefined}
                  >
                    {column.label}
                  </th>
                )
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={index} className="tabela-dinamica-row">
                {columns.map((column) => {
                  if (column.key === 'actions' && config.permission) {
                    return (
                      <PermissionGate key={column.key} required={config.permission}>
                        <td className="text-center">
                          <button
                            className="btn btn-link p-0"
                            onClick={() => handleNavigation(row)}
                            title="Ver detalhes"
                          >
                            <FontAwesomeIcon icon={faEye} className="fa-eye" />
                          </button>
                        </td>
                      </PermissionGate>
                    );
                  }
 
                  const value = row[column.key];
                  let formattedValue = value;
                 
                  if (column.type === 'percentage') {
                    formattedValue = `${(value * 100).toFixed(2)}%`;
                  } else if (column.type === 'number' && column.format) {
                    formattedValue = new Intl.NumberFormat(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    }).format(value);
                  }
 
                  const colorStyle: React.CSSProperties = {};
                  if (column.colorCondition) {
                    for (const [condition, color] of Object.entries(column.colorCondition)) {
                      if (condition.startsWith('>') && value > parseFloat(condition.slice(1))) {
                        colorStyle.color = color;
                      } else if (condition.startsWith('<') && value < parseFloat(condition.slice(1))) {
                        colorStyle.color = color;
                      }
                    }
                  }
 
                  return (
                    <td className="tabela-dinamica-cell"
                      key={column.key}
                      style={colorStyle}
                      data-type={column.type}
                    >
                      {formattedValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
 
      {config?.pagination?.enabled && totalPages > 1 && (
        <nav>
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(1)}>«</button>
            </li>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>‹</button>
            </li>
           
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
             
              return (
                <li key={i} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                    {pageNum}
                  </button>
                </li>
              );
            })}
 
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>›</button>
            </li>
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(totalPages)}>»</button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};
 
export default TabelaDinamica;